// src/context/AuthContext.js
import { createContext, useEffect, useState, useCallback } from "react";
import { jwtDecode } from "jwt-decode"; // <- IMPORTANT: default import

export const AuthContext = createContext();

const USE_LOCALSTORAGE_FALLBACK = false; // set true to persist access token across refreshes (less secure)

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(null);
  const [errors, setErrors] = useState({
    login: null,
    refresh: null,
    users: null,
  });
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // block rendering until we attempt restore

  const decodeTokenUserInfo = (token) => {
    try {
      const decoded = jwtDecode(token);
      return decoded?.UserInfo ?? null;
    } catch (e) {
      console.debug("decodeTokenUserInfo failed:", e?.message);
      return null;
    }
  };

  const saveAccessTokenFallback = (token) => {
    if (USE_LOCALSTORAGE_FALLBACK) {
      if (token) localStorage.setItem("accessToken", token);
      else localStorage.removeItem("accessToken");
    }
  };

  const login = async (username, password) => {
    setErrors((p) => ({ ...p, login: null }));
    try {
      const response = await fetch("/api/auth", {
        method: "POST",
        credentials: "include", // important: allow server to set refresh cookie
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Login failed");
      }

      const data = await response.json();
      const userInfo = decodeTokenUserInfo(data.accessToken);

      setAccessToken(data.accessToken);
      setUser(userInfo);
      setIsAuthenticated(true);
      saveAccessTokenFallback(data.accessToken);

      return data;
    } catch (err) {
      setErrors((p) => ({ ...p, login: err.message }));
      setIsAuthenticated(false);
      setAccessToken(null);
      setUser(null);
      saveAccessTokenFallback(null);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.debug("logout request failed:", e?.message);
    }

    setAccessToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setUsers([]);
    saveAccessTokenFallback(null);
  };

  // returns new token or null
  const refreshToken = useCallback(async () => {
    try {
      // Attempt refresh via cookie
      const res = await fetch("/api/auth/refresh", {
        method: "GET",
        credentials: "include", // send cookies
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.message || `Refresh failed (${res.status})`);
      }

      const data = await res.json();
      const userInfo = decodeTokenUserInfo(data.accessToken);

      setAccessToken(data.accessToken);
      setUser(userInfo);
      setIsAuthenticated(true);
      setErrors((p) => ({ ...p, refresh: null }));
      saveAccessTokenFallback(data.accessToken);
      return data.accessToken;
    } catch (err) {
      // If cookie-based refresh fails, optionally attempt localStorage fallback
      console.debug("refreshToken error:", err?.message);

      if (USE_LOCALSTORAGE_FALLBACK) {
        const stored = localStorage.getItem("accessToken");
        if (stored) {
          const decoded = (() => {
            try {
              return jwtDecode(stored);
            } catch {
              return null;
            }
          })();

          // only accept fallback token if it is still valid
          if (decoded && decoded.exp * 1000 > Date.now()) {
            const userInfo = decoded.UserInfo ?? null;
            setAccessToken(stored);
            setUser(userInfo);
            setIsAuthenticated(true);
            setErrors((p) => ({ ...p, refresh: null }));
            console.debug("Used localStorage accessToken fallback");
            return stored;
          }
        }
      }

      setErrors((p) => ({ ...p, refresh: err.message }));
      setIsAuthenticated(false);
      setAccessToken(null);
      setUser(null);
      saveAccessTokenFallback(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getAllUsers = async () => {
    if (!accessToken) {
      setErrors((p) => ({ ...p, users: "Not authenticated" }));
      return;
    }

    try {
      const res = await fetch("/api/users", {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.message || `Fetch users failed (${res.status})`);
      }
      const data = await res.json();
      setUsers(data);
      setErrors((p) => ({ ...p, users: null }));
    } catch (err) {
      setErrors((p) => ({ ...p, users: err.message }));
      setUsers([]);
    }
  };

  const adminCreateUser = async (userData) => {
    if (!accessToken) throw new Error("Not authenticated");
    const res = await fetch("/api/users", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(userData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Admin user creation failed");
    await getAllUsers();
    return data;
  };

  const updateUserPassword = async (id, password) => {
    if (!accessToken) throw new Error("Not authenticated");
    const res = await fetch(`/api/users/${id}/password`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || "Failed to update password");
    }
    return await res.json();
  };

  // schedule refresh 60s before expiry
  useEffect(() => {
    if (!accessToken) return;
    let decoded;
    try {
      decoded = jwtDecode(accessToken);
    } catch {
      return;
    }
    const expMs = decoded.exp * 1000;
    const refreshAt = expMs - Date.now() - 60000; // 60s before expiry
    if (refreshAt <= 0) {
      // token already near/expired -> refresh now
      refreshToken();
      return;
    }
    const t = setTimeout(() => refreshToken(), refreshAt);
    return () => clearTimeout(t);
  }, [accessToken, refreshToken]);

  // Try to restore session on mount
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const token = await refreshToken(); // attempts cookie-based refresh
      if (!token && USE_LOCALSTORAGE_FALLBACK) {
        // fallback already handled inside refreshToken
      }
      // final isLoading false happens inside refreshToken's finally
    })();
  }, [refreshToken]);

  // Block rendering until initial attempt completes
  if (isLoading) {
    return <div>Restoring session...</div>;
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        accessToken,
        user,
        users,
        errors,
        login,
        logout,
        getAllUsers,
        adminCreateUser,
        updateUserPassword,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
